import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { formatCurrencyUSD } from '@/components/postgrid/common/dateUtils';
import { RecentMailWarning } from '@/components/postgrid/common/RecentMailWarning';
import { ContactComboboxWithCreate } from '@/components/postgrid/contact-combobox/ContactComboboxWithCreate';
import { WizardStep } from '@/components/postgrid/wizard';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import { Input, InputGroup } from '@/components/ui/catalyst/input';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { CHEQUE_MAX_AMOUNT, CHEQUE_MIN_AMOUNT } from '@/constants/postgrid';
import { useMatter } from '@/hooks/useMatter';
import { useGetFirmQuery } from '@/services/api/firmsService';
import { enhancedApi } from '@/services/api/generated/postgridService';
import { useGetMatterQuery } from '@/services/api/mattersService';

/** Creates the validation schema with dynamic max amount from feature flag */
const createRecipientAmountStepSchema = (maxAmount: number) =>
  z.object({
    to_contact_id: z.string().min(1, 'Recipient contact is required'),
    amount: z
      .string()
      .min(1, 'Amount is required')
      .refine(
        (val) => {
          const num = parseFloat(val);
          return (
            !Number.isNaN(num) && num >= CHEQUE_MIN_AMOUNT && num <= maxAmount
          );
        },
        `Amount must be between $${CHEQUE_MIN_AMOUNT.toFixed(2)} and $${maxAmount.toFixed(2)}`,
      ),
    memo: z.string().optional(),
    use_return_envelope: z.boolean().default(false),
  });

type RecipientAmountStepData = z.infer<
  ReturnType<typeof createRecipientAmountStepSchema>
>;

interface ChequeRecipientAmountStepProps {
  wizardData?: {
    to_contact_id?: string;
    to_contact_name?: string;
    amount?: string;
    memo?: string;
    use_return_envelope?: boolean;
  };
  updateStepData?: (data: Record<string, unknown>) => void;
  markStepComplete?: (isValid: boolean) => void;
}

export const ChequeRecipientAmountStep: React.FC<
  ChequeRecipientAmountStepProps
> = ({ wizardData = {}, updateStepData, markStepComplete }) => {
  const { matterId } = useMatter();

  // Fetch matter details for auto-filling memo and getting firm ID
  const { data: matterDetails } = useGetMatterQuery(matterId || '', {
    skip: !matterId,
  });

  // Fetch firm to get feature flags for dynamic cheque max amount
  const firmId = matterDetails?.firm?.id;
  const { data: firm } = useGetFirmQuery(firmId || '', {
    skip: !firmId,
  });

  // Get dynamic max amount from feature flag, fallback to constant
  const chequeMaxAmount = useMemo(() => {
    const flagValue = firm?.feature_flags?.cheque_max_amount;
    if (flagValue) {
      const parsed = parseFloat(flagValue);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return CHEQUE_MAX_AMOUNT;
  }, [firm?.feature_flags?.cheque_max_amount]);

  // Create schema with dynamic max amount
  const recipientAmountStepSchema = useMemo(
    () => createRecipientAmountStepSchema(chequeMaxAmount),
    [chequeMaxAmount],
  );

  // Track the last contact ID we auto-filled for to prevent re-triggering
  const lastAutoFilledContactRef = useRef<string | null>(null);

  const {
    control,
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<RecipientAmountStepData>({
    resolver: zodResolver(recipientAmountStepSchema),
    defaultValues: {
      to_contact_id: wizardData.to_contact_id || '',
      amount: wizardData.amount || '',
      memo: wizardData.memo || '',
      use_return_envelope: wizardData.use_return_envelope || false,
    },
    mode: 'onChange',
  });

  const watchedToContact = watch('to_contact_id');
  const watchedAmount = watch('amount');
  const watchedMemo = watch('memo');
  const watchedUseReturnEnvelope = watch('use_return_envelope');

  // Re-validate amount when max limit changes (e.g., after firm data loads)
  useEffect(() => {
    if (watchedAmount) {
      trigger('amount');
    }
  }, [chequeMaxAmount, trigger, watchedAmount]);

  // Fetch contact details - load all contacts immediately
  const { data: contactsResponse } = enhancedApi.usePostgridContactsListQuery(
    { q: '', isActive: true, limit: 100 },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const contactsList = contactsResponse?.results || [];

  // Find the selected contact from the list
  const contactDetails = contactsList?.find(
    (contact) => contact.id === watchedToContact,
  );

  const formatCurrency = (value: string): string => {
    const numericValue = value.replace(/[^0-9.]/g, '');
    const parsed = parseFloat(numericValue);
    if (Number.isNaN(parsed)) return '';
    return parsed.toFixed(2);
  };

  // Update wizard data when form values change
  useEffect(() => {
    if (updateStepData) {
      updateStepData({
        to_contact_id: watchedToContact,
        amount: watchedAmount,
        memo: watchedMemo,
        use_return_envelope: watchedUseReturnEnvelope,
      });
    }
  }, [
    watchedToContact,
    watchedAmount,
    watchedMemo,
    watchedUseReturnEnvelope,
    updateStepData,
  ]);

  // Mark step as complete/incomplete based on validation
  useEffect(() => {
    markStepComplete?.(
      isValid && Boolean(watchedToContact) && Boolean(watchedAmount),
    );
  }, [isValid, watchedToContact, watchedAmount, markStepComplete]);

  // Auto-fill memo when contact selection changes
  useEffect(() => {
    if (
      watchedToContact &&
      watchedToContact !== lastAutoFilledContactRef.current &&
      matterDetails
    ) {
      // Auto-fill memo for all contacts with matter context
      const matterName = matterDetails.name || 'case';
      const dateOfLoss = matterDetails.date_of_loss
        ? new Date(matterDetails.date_of_loss).toLocaleDateString()
        : '';

      const autoMemo = dateOfLoss
        ? `Police report request re: ${matterName} - ${dateOfLoss}`
        : `Police report request re: ${matterName}`;

      setValue('memo', autoMemo);

      lastAutoFilledContactRef.current = watchedToContact;
    }
  }, [matterDetails, watchedToContact, setValue]);

  const handleToContactChange = (contactId: string | null) => {
    // Update step data with contact selection
    if (updateStepData) {
      const selectedContact = contactsList?.find(
        (contact) => contact.id === contactId,
      );

      const firstName = selectedContact?.first_name;
      const companyName = selectedContact?.company_name;
      const fallbackFirstName = contactDetails?.first_name;
      const fallbackCompanyName = contactDetails?.company_name;

      updateStepData({
        to_contact_id: contactId || '',
        to_contact_name:
          firstName ||
          companyName ||
          fallbackFirstName ||
          fallbackCompanyName ||
          'Selected Contact',
      });
    }
  };

  return (
    <WizardStep
      title="Cheque Recipient & Amount"
      description="Select the recipient and enter payment details."
    >
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">
          Payment Details
        </h4>

        <div className="space-y-4">
          {/* Recipient Selection */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-shrink-0 w-24">
              <span className="text-sm text-gray-600">Recipient *</span>
            </div>
            <div className="flex-1 min-w-0">
              <Controller
                name="to_contact_id"
                control={control}
                render={({ field }) => (
                  <ContactComboboxWithCreate
                    value={field.value}
                    onChange={(contactId, contactName) => {
                      field.onChange(contactId);
                      handleToContactChange(contactId);
                      // Update step data with contact name if provided
                      if (contactName && updateStepData) {
                        updateStepData({
                          to_contact_id: contactId || '',
                          to_contact_name: contactName,
                        });
                      }
                    }}
                    placeholder="Select recipient..."
                    aria-label="Select recipient contact"
                    className="w-full"
                  />
                )}
              />
              {errors.to_contact_id && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.to_contact_id.message}
                </p>
              )}
            </div>
          </div>

          {/* Recent Mail Warning */}
          {watchedToContact && matterId && (
            <RecentMailWarning
              matterId={matterId}
              contactId={watchedToContact}
              lookbackDays={30}
            />
          )}

          {/* Amount Field */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0 w-24">
              <span className="text-sm text-gray-600">Amount *</span>
            </div>
            <div className="flex-1 max-w-48">
              <InputGroup>
                <span data-slot="icon" className="font-medium text-gray-700">
                  $
                </span>
                <Input
                  type="number"
                  step="0.01"
                  min={CHEQUE_MIN_AMOUNT.toString()}
                  max={chequeMaxAmount.toString()}
                  {...register('amount')}
                  placeholder="0.00"
                  className="text-right"
                  onBlur={(e) => {
                    const formatted = formatCurrency(e.target.value);
                    if (formatted) {
                      setValue('amount', formatted);
                    }
                  }}
                />
              </InputGroup>
              <div className="mt-1 flex justify-between items-center text-xs">
                <span className="text-gray-500">
                  Max: ${chequeMaxAmount.toFixed(2)}
                </span>
                {watchedAmount && (
                  <span className="font-medium text-gray-700">
                    {formatCurrencyUSD(watchedAmount)}
                  </span>
                )}
              </div>
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.amount.message}
                </p>
              )}
            </div>
          </div>

          {/* Memo Field */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-shrink-0 w-24">
              <span className="text-sm text-gray-600">Memo</span>
            </div>
            <div className="flex-1 min-w-0">
              <Textarea
                {...register('memo')}
                rows={2}
                placeholder="e.g., Police records request, Case #12345"
                className="resize-none"
              />
              {errors.memo && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.memo.message}
                </p>
              )}
            </div>
          </div>

          {/* Return Envelope Option */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0 w-24">
              <span className="text-sm text-gray-600">Options</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Controller
                  name="use_return_envelope"
                  control={control}
                  render={({ field }) => (
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  )}
                />
                <span className="text-sm text-gray-700">
                  Include return envelope
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WizardStep>
  );
};
